import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, DataTable, ConfirmationModal, PageHeader, useDataTableUrl, type DataTableColumn } from '@airclou/ui';
import { Building2, Plus, Users, LayoutGrid, Group } from 'lucide-react';
import { listCompanies, deleteCompany, type Company } from '@/api/companies';
import { CreateCompanyModal } from '../../components/hierarchy/CreateCompanyModal';
import { EditCompanyModal } from '../../components/hierarchy/EditCompanyModal';
import { ErrorState } from '../../components/state/ErrorState';
import { useTimezone } from '@airclou/shell/timezone'
import { formatDateInTz } from '@airclou/utils'
import { useAuth } from '../../contexts/AuthContext'
import { canManageOrg } from '../../navigation'
import { RoleUnknownNotice } from '../../components/state/RoleUnknownNotice';

export function CompaniesPage() {
  const timezone = useTimezone()
  const { t } = useTranslation('dashboard');
  const { user } = useAuth();

  // `POST /api/companies` is `TENANT_ROLE_SETS.MANAGE` as of 2026-09-13, so
  // offering Create to anyone else is offering a 403. `canManageOrg` mirrors
  // that role set. Reading the list stays open to any member — only the create
  // moves. (Delete is owner-only and lives on the company's own settings tab;
  // the confirmation modal further down this file is a parked stub that nothing
  // opens.)
  const canManage = canManageOrg(user?.orgRole ?? undefined);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [deletingCompany, setDeletingCompany] = useState<Company | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  // Sort lives in the URL, so it survives a reload, a shared link and back/forward.
  const { sorting, onSortingChange } = useDataTableUrl();

  // Only the newest load may write state: a reload started after a create can
  // finish before the first load, and the older answer must not overwrite it.
  const latestLoad = useRef(0);

  const loadCompanies = async () => {
    const loadId = ++latestLoad.current;
    try {
      setLoading(true);
      setError(null);
      const response = await listCompanies();
      if (loadId !== latestLoad.current) return;
      setCompanies(response.companies);
    } catch (err: any) {
      if (loadId !== latestLoad.current) return;
      setError(err.message || t('companies.failedToLoad'));
    } finally {
      if (loadId === latestLoad.current) setLoading(false);
    }
  };

  useEffect(() => {
    loadCompanies();
  }, []);

  const handleDelete = async () => {
    if (!deletingCompany) return;

    try {
      setIsDeleting(true);
      await deleteCompany(deletingCompany.id);
      setDeletingCompany(null);
      loadCompanies();
    } catch (err: any) {
      setError(err.message || t('companies.failedToDelete'));
    } finally {
      setIsDeleting(false);
    }
  };

  const columns: DataTableColumn<Company>[] = [
    {
      id: 'name',
      header: t('common.name'),
      size: 300,
      isFlexible: true,
      accessorKey: 'name',
      enableSorting: true,
      cell: (company: Company) => (
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/15 dark:bg-primary/25">
            <Building2 className="w-4 h-4 text-primary" />
          </div>
          <div>
            <div className="font-medium text-foreground">{company.name}</div>
            <div className="text-sm text-muted-foreground">{company.slug}</div>
          </div>
        </div>
      ),
    },
    {
      id: 'workspaces',
      header: t('common.workspaces'),
      size: 120,
      accessorFn: (company: Company) => company.workspace_count ?? 0,
      enableSorting: true,
      cell: (company: Company) => (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <LayoutGrid className="w-4 h-4" />
          <span>{company.workspace_count ?? 0}</span>
        </div>
      ),
    },
    {
      id: 'members',
      header: t('common.members'),
      size: 100,
      accessorFn: (company: Company) => company.member_count ?? 0,
      enableSorting: true,
      cell: (company: Company) => (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Users className="w-4 h-4" />
          <span>{company.member_count ?? 0}</span>
        </div>
      ),
    },
    {
      id: 'groups',
      header: t('common.groups'),
      size: 100,
      accessorFn: (company: Company) => company.group_count ?? 0,
      enableSorting: true,
      cell: (company: Company) => (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Group className="w-4 h-4" />
          <span>{company.group_count ?? 0}</span>
        </div>
      ),
    },
    {
      id: 'created_at',
      header: t('common.created'),
      size: 120,
      accessorKey: 'created_at',
      enableSorting: true,
      cell: (company: Company) => (
        <div className="text-sm text-muted-foreground">
          {formatDateInTz(company.created_at, timezone)}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('companies.title')}
        description={t('companies.subtitle')}
        actions={
          canManage ? (
            <Button onClick={() => setIsCreateModalOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              {t('companies.createCompany')}
            </Button>
          ) : undefined
        }
      />

      <RoleUnknownNotice />

      {/* Companies Table — only this region waits on the fetch; the header paints at once. */}
      <div>
        {error ? (
          <ErrorState
            title={t('companies.failedToLoad')}
            description={error}
            onRetry={loadCompanies}
          />
        ) : !loading && companies.length === 0 ? (
          <div className="p-12 text-center">
            <Building2 className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">
              {t('companies.noCompaniesYet')}
            </h3>
            <p className="text-sm text-muted-foreground mb-6">
              {t('companies.getStarted')}
            </p>
            {canManage && (
              <Button onClick={() => setIsCreateModalOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                {t('companies.createCompany')}
              </Button>
            )}
          </div>
        ) : (
          <DataTable
            data={companies}
            columns={columns}
            sorting={sorting}
            onSortingChange={onSortingChange}
            getRowHref={(company) => `/companies/${company.id}`}
            // Spinner row only on first load; a reload after create keeps the rows.
            isLoading={loading && companies.length === 0}
          />
        )}
      </div>

      {/* Modals */}
      <CreateCompanyModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={() => {
          setIsCreateModalOpen(false);
          loadCompanies();
        }}
      />

      {editingCompany && (
        <EditCompanyModal
          company={editingCompany}
          isOpen={!!editingCompany}
          onClose={() => setEditingCompany(null)}
          onSuccess={() => {
            setEditingCompany(null);
            loadCompanies();
          }}
        />
      )}

      {deletingCompany && (
        <ConfirmationModal
          isOpen={!!deletingCompany}
          onClose={() => setDeletingCompany(null)}
          title={t('companies.deleteCompany')}
          description={t('companies.deleteConfirm', { name: deletingCompany.name })}
          variant="danger"
          confirmText={t('common.delete')}
          cancelText={t('common.cancel')}
          onConfirm={handleDelete}
          isLoading={isDeleting}
        />
      )}
    </div>
  );
}
